import { useEffect, useRef, useState } from 'react';
import { useRoom } from '../net/useRoom.js';
import { stagePlayer } from './stagePlayer.js';

/**
 * useNarration — bridges the room's narration state to the Stage player.
 *
 * When `enabled` is true (Stage only), each new narration beat is forwarded to
 * the stagePlayer queue. The hook returns the current caption text and speaking
 * state so the Stage UI can render captions.
 *
 * Design:
 * - Deduplication: the same narration object reference re-entering the effect
 *   doesn't re-enqueue (the stableRef guard).
 * - Caption is always available regardless of audio unlock state.
 * - Hands always pass enabled=false → zero audio, caption through useRoom
 *   (narration store) directly.
 *
 * @param enabled - only the Stage passes true; Hands leave audio silent.
 */
export function useNarration(enabled: boolean): {
  caption: string | null;
  unlock: () => void;
  isUnlocked: boolean;
  isSpeaking: boolean;
} {
  const narration = useRoom((s) => s.narration);
  // Track which audioId we last enqueued to avoid double-queueing on re-renders.
  const lastEnqueued = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    if (!enabled || !narration) return;
    // Build a stable key for this beat: audioId when available, else text hash.
    const key = narration.audioId ?? narration.text;
    if (lastEnqueued.current === key) return;
    lastEnqueued.current = key;
    stagePlayer.enqueue(narration.audioId, narration.text);
  }, [enabled, narration]);

  // Reactive speaking state: subscribe to the player's real playback transitions
  // instead of guessing from the (persistent) narration record. This is what lets
  // the host status flip back out of "Narrating…" once a clip finishes.
  const [speaking, setSpeaking] = useState(false);
  useEffect(() => {
    if (!enabled) return;
    setSpeaking(stagePlayer.isSpeaking);
    return stagePlayer.onSpeaking(setSpeaking);
  }, [enabled]);

  return {
    caption: narration?.text ?? null,
    unlock: () => stagePlayer.unlock(),
    isUnlocked: stagePlayer.isUnlocked,
    isSpeaking: enabled && speaking,
  };
}
